import {
  DropdownButtonType,
  List,
  ListItem,
  ListProps,
  SingleSelectDropdown
} from '@tableau/widgets';
import * as React from 'react';

import { TypeUnsafeTranslation } from '~src/common/react/IReactTranslateService';
import { ISelectFilterDefinition } from '~src/content/services/FilterPanelService';

import { ClearFilterButton } from './ClearFilterButton';
import { IFilterControl, submitUrlQuery } from './FilterPanel';
import { dropdownListContainer, filterComponent, filterWrapper } from './FilterPanelStyles';
import { ListItemContentWithCount } from './ListItemContentWithCount';

export interface SelectDropdownFilterProps {
  readonly filterControl: IFilterControl; // Expect to have definition of type ISelectFilterDefinition<any>
  readonly shouldClear: boolean;
  readonly showContentCount: boolean;
}

export function SelectDropdownFilter(props: SelectDropdownFilterProps) {
  const { filterControl } = props;
  const defaultValue = (filterControl.definition as ISelectFilterDefinition<any>).defaultValue;
  const options = (filterControl.definition as ISelectFilterDefinition<any>).options ?? [];

  const buttonRef = React.useRef<HTMLButtonElement>();
  const [selectedValue, setSelectedValue] = React.useState<string>(filterControl.value?.toString());
  const [selectedLabel, setSelectedLabel] = React.useState<string>('');

  const listProps = React.useMemo(
    (): ListProps => {
      const listItems: ListItem[] = options.map(
        (option) => {
          const label = TypeUnsafeTranslation.translate(option.translate);

          return {
            content: props.showContentCount ?
              <ListItemContentWithCount label={label} count={option.count} /> :
              <>{label}</>,
            value: option.value,
            testId: 'list-item-' + option.value
          };
        }
      );

      return {
        items: listItems,
      };
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options]
  );

  // Needed for updates from the URL
  React.useEffect(
    () => {
      const updatedValue = filterControl.value;
      if (selectedValue !== updatedValue) {
        setSelectedValue(updatedValue ?? '');
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterControl.value]
  );

  // set label on mount
  React.useEffect(
    () => {
      setLabel();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // update label when the selected value changes
  React.useEffect(
    () => {
      setLabel();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedValue]
  );

  // Listen for clearing all filters
  React.useEffect(
    () => {
      if (props.shouldClear) {
        setSelectedValue(defaultValue);
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.shouldClear]
  );

  const handleChange = (selection: string) => {
    setSelectedValue(selection);

    const params = filterControl.definition.valueToParams(selection);
    submitUrlQuery(params);
  };

  const setLabel = () => {
    if (selectedValue) {
      const item = options.find(item => item.value.toString() === selectedValue);
      setSelectedLabel(item ? TypeUnsafeTranslation.translate(item.translate) : '');
    }
  };

  const clearFilter = () => {
    handleChange(defaultValue);
  };

  return <div className={filterWrapper}>
    <SingleSelectDropdown
      buttonRef={buttonRef}
      children={selectedLabel}
      className={filterComponent}
      label={TypeUnsafeTranslation.translate(filterControl.definition.name)}
      list={<List
        className={dropdownListContainer}
        {...listProps}
      />}
      onChange={handleChange}
      testId={'select-filter-' + filterControl.definition.name}
      type={DropdownButtonType.Line}
      value={selectedValue}
    />
    {selectedValue !== defaultValue &&
      <ClearFilterButton
        clearFilter={clearFilter}
        filterName={filterControl.definition.name}
        getElementToRefocus={() => buttonRef.current}
      />
    }
  </div>;
}
